import React, { useEffect, useState } from "react";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useApiService from "../../../hooks/useApiService";
import { ADMIN_PAGE_BOOST_AD_LIMIT } from "../../../configs/limits/adLimits";
import {
  approveBoostRequest,
  getBoostRequestedClassifieds,
} from "../../../services/admin-service/adminService";
import { AdminBoost } from "../../../types/admin-types/adminTypes";
import useAuthorization from "../../../hooks/useAuthorization";
import { UserRole } from "../../../enums/UserRole";
import { toastError, toastSuccess } from "../../../utils/toast";
import { FaChevronLeft, FaChevronRight, FaEllipsisV } from "react-icons/fa";
import { formatEpochTime } from "../../../utils/timeConvertors";
import "../../../styles/css/data-table.css";

function AdminBoostreqPage() {
  useAuthorization([UserRole.ADMIN, UserRole.ROOT]);
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  let page = searchParams.get("page") || "1";
  let payload = {
    limit: ADMIN_PAGE_BOOST_AD_LIMIT,
    skip: 0 + (Number(page) - 1) * ADMIN_PAGE_BOOST_AD_LIMIT,
  };
  let [adList, setAdList] = useState<AdminBoost[]>([]);

  const { data } = useApiService({
    serviceCall: () => getBoostRequestedClassifieds(payload),
    dependencies: [page],
  });

  async function handleActionBtn(adDetails: AdminBoost, btnType: string) {
    let payload = {
      classified_id: adDetails.classified_id,
      verified: btnType === "approve",
    };
    let approvalStatus = btnType === "approve" ? "approved" : "disapproved";
    try {
      await approveBoostRequest(payload);
      toastSuccess(`You've successfully ${approvalStatus} the boost request!`);
      setAdList((prev) =>
        prev.filter((ad) => ad.classified_id !== adDetails.classified_id)
      );
    } catch (error: any) {
      toastError(error.response.data.message);
    }
  }

  function handleNext() {
    setSearchParams({ page: (Number(page) + 1).toString() });
  }

  function handlePrevious() {
    if (Number(page) - 1 > 0) {
      setSearchParams({ page: (Number(page) - 1).toString() });
    }
  }

  useEffect(() => {
    if (data?.boost_requests) {
      setAdList(data.boost_requests);
    }
  }, [data]);

  function boostTypeBadge(boostType: string) {
    if (boostType === "featured") return "status-badge status-badge--featured";
    return "status-badge status-badge--standard";
  }

  function boostTypeLabel(boostType: string) {
    return boostType === "boosted" ? "standard" : boostType;
  }

  return (
    <div className="data-page">
      <div className="container">
        <div className="data-page__card">
          {/* Header */}
          <div className="data-page__header">
            <div className="data-page__title-group">
              <button
                className="data-page__back-btn"
                onClick={() => navigate("/admin/dashboard")}
              >
                <MdOutlineArrowBackIosNew size={18} />
              </button>
              <div>
                <h4 className="data-page__title">Boost Requests</h4>
                <p className="data-page__subtitle">Review pending boost applications</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Ad</th>
                  <th>Seller</th>
                  <th>Requested Date</th>
                  <th>Boost Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!adList.length ? (
                  <tr>
                    <td colSpan={6} className="data-table-empty">
                      No pending boost requests
                    </td>
                  </tr>
                ) : (
                  adList.map((ad, index) => (
                    <tr key={ad.classified_id ?? index}>
                      <th scope="row">{index + 1}</th>
                      <td>
                        <Link to={`/ads/${ad.classified_id}`} className="listing-link">
                          {[ad.model, ad.make, ad.year].filter(Boolean).join(" ")}
                        </Link>
                      </td>
                      <td>
                        <Link to={`/seller-profile/${ad.seller_id}`} className="listing-link">
                          {ad.seller_name}
                        </Link>
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.8125rem" }}>
                        {formatEpochTime(ad.boost_requested_at)}
                      </td>
                      <td>
                        <span className={boostTypeBadge(ad.boost_type)}>
                          {boostTypeLabel(ad.boost_type)}
                        </span>
                      </td>
                      <td>
                        <div className="dropdown">
                          <button
                            className="action-menu-btn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <FaEllipsisV size={14} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleActionBtn(ad, "approve")}
                              >
                                Approve
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() => handleActionBtn(ad, "disapprove")}
                              >
                                Disapprove
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="data-page__pagination">
            <button
              className="data-page__nav-btn"
              disabled={Number(page) === 1}
              onClick={handlePrevious}
            >
              <FaChevronLeft size={11} /> Previous
            </button>
            <span className="data-page__page-info">Page {page}</span>
            <button
              className="data-page__nav-btn"
              disabled={data?.is_last_page}
              onClick={handleNext}
            >
              Next <FaChevronRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminBoostreqPage;
